import { confirm, DataTable, type TanstackReactTable } from "@devhop/ui";
import { EditIcon, TrashIcon } from "lucide-react";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { AppImage } from "@/shared/ui/AppImage";
import { RowActions } from "@/shared/ui/RowActions";
import type { CatalogSkuDTO } from "../api/client";

const CONDITION_LABELS: Record<string, string> = {
  new: "ໃໝ່",
  used: "ໃຊ້ແລ້ວ",
  refurb: "Refurb",
};

type CatalogSkusTableProps = {
  skus: CatalogSkuDTO[];
  canManage: boolean;
  isLoading: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onEdit: (sku: CatalogSkuDTO) => void;
  onDelete: (skuId: string) => Promise<void>;
  onManageFitments: (skuId: string) => void;
  onManageSkuEpc: (skuId: string) => void;
  onManageMedia: (skuId: string) => void;
  onPaginationChange: (offset: number, limit: number) => void;
};

export function CatalogSkusTable({
  skus,
  canManage,
  isLoading,
  offset,
  limit,
  totalCount,
  onEdit,
  onDelete,
  onManageFitments,
  onManageSkuEpc,
  onManageMedia,
  onPaginationChange,
}: CatalogSkusTableProps) {
  const columns: TanstackReactTable.ColumnDef<CatalogSkuDTO>[] = [
    {
      id: "order",
      header: "ລຳດັບ",
      cell: ({ row }) => offset + row.index + 1,
      size: 70,
    },
    {
      id: "thumbnail",
      header: "ຮູບ",
      cell: ({ row }) => {
        const url = row.original.thumbnailUrl;
        if (!url?.trim()) {
          return (
            <div className="flex size-12 items-center justify-center rounded border border-muted-foreground/30 border-dashed bg-muted/20 text-muted-foreground text-xs">
              —
            </div>
          );
        }
        return (
          <AppImage
            src={url}
            alt=""
            className="size-12 shrink-0 rounded border border-border/60 object-cover"
            fit="cover"
          />
        );
      },
      size: 80,
    },
    {
      id: "oemPartNo",
      header: "OEM Part No",
      cell: ({ row }) => row.original.oemPartNo ?? "—",
      size: 160,
    },
    {
      id: "condition",
      header: "ສະພາບ",
      cell: ({ row }) =>
        row.original.condition
          ? (CONDITION_LABELS[row.original.condition] ?? row.original.condition)
          : "—",
      size: 100,
    },
    {
      id: "createdAt",
      header: "ວັນທີ່ສ້າງ",
      cell: ({ row }) => formatDateTimeLocal(row.original.createdAt),
      size: 120,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        if (!canManage) return null;
        const sku = row.original;
        const actions = [
          {
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="size-4" />,
            onClick: () => onEdit(sku),
          },
          {
            label: "EPC / ພາກ",
            onClick: () => onManageSkuEpc(sku.id),
          },
          {
            label: "Fitments",
            onClick: () => onManageFitments(sku.id),
          },
          {
            label: "ຮູບ",
            onClick: () => onManageMedia(sku.id),
          },
          {
            label: "ລຶບ",
            variant: "destructive" as const,
            icon: <TrashIcon className="size-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບ SKU",
                description: "ຖ້າມີຮ້ານໃຊ້ SKU ນີ້ຢູ່ ຈະລຶບບໍ່ໄດ້. ຕ້ອງການລຶບບໍ?",
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) await onDelete(sku.id);
            },
          },
        ];
        return <RowActions actions={actions} maxInline={4} />;
      },
      size: 140,
    },
  ];

  return (
    <DataTable<CatalogSkuDTO, unknown>
      noDataMessage="ຍັງບໍ່ມີ SKU ຫຼັກ — ກົດ ເພີ່ມ SKU ເພີ່ມເຂົ້າມາ"
      isLoading={isLoading}
      columns={columns}
      data={skus}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      enableSorting={false}
      sortBy="order"
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
